import { fileRevision, sameFileRevision } from './fileRevision.js';
import { parseRep, REP_PARSER_VERSION } from './parseRep.js';
import { MAX_REP_BYTES } from './studyFiles.js';

export const studyLabel = (study) =>
  `${study.studyId ? `Study ID: ${study.studyId}` : 'Study ID unavailable'} — ${study.stem}`;

// Keep all exports behind the single dropdown entry for PDF-specific selection.
export function studyExports(studies) {
  return studies.flatMap((study) => study.exportCandidates || [study]);
}

// Keep the newest REP export for each Study ID, including its own QRS references.
// Unknown IDs remain separate; never combine channels from different exports.
export function newestStudies(studies) {
  const selected = new Map();
  const unknown = [];
  const groups = new Map();
  for (const study of studyExports(studies)) {
    const key = String(study.studyId || '').trim();
    if (!key) {
      unknown.push(study);
      continue;
    }
    if (!groups.has(key)) groups.set(key, new Map());
    groups.get(key).set(study.id, study);
    const current = selected.get(key);
    const modified = (value) =>
      Number(value.repRevision?.lastModified ?? value.rep?.lastModified) || 0;
    if (
      !current ||
      modified(study) > modified(current) ||
      (modified(study) === modified(current) &&
        String(study.id).localeCompare(String(current.id)) > 0)
    ) {
      selected.set(key, study);
    }
  }
  const grouped = [...selected.entries()].map(([key, study]) => {
    const exports = [...groups.get(key).values()];
    return exports.length > 1 ? { ...study, exportCandidates: exports } : study;
  });
  return [...grouped, ...unknown].sort(
    (a, b) =>
      a.stem.localeCompare(b.stem, undefined, { numeric: true }) || a.folder.localeCompare(b.folder)
  );
}

// Read only REP metadata during discovery; leave every QRS handle unopened.
export async function loadStudyIds(studies, isCancelled = () => false) {
  const result = [];
  for (const study of studies) {
    if (isCancelled()) throw new Error('Folder scan cancelled.');
    let studyId = '';
    let parsedMetadata = null;
    let repRevision = null;
    let metadataWarning = '';
    try {
      if (!study.rep) throw new Error('REP file is missing.');
      const file = study.rep.handle ? await study.rep.handle.getFile() : study.rep;
      if (isCancelled()) throw new Error('Folder scan cancelled.');
      if (file.size > MAX_REP_BYTES) throw new Error('REP exceeds size limit.');
      if (file.name.toLowerCase() !== study.rep.name.toLowerCase())
        throw new Error('REP filename changed.');
      const buffer = await file.arrayBuffer();
      if (isCancelled()) throw new Error('Folder scan cancelled.');
      parsedMetadata = parseRep(buffer, study.stem);
      repRevision = fileRevision(file);
      studyId = parsedMetadata.meta.study;
      if (!studyId) {
        metadataWarning =
          parsedMetadata.studyIdWarning ||
          parsedMetadata.warnings?.[0] ||
          'REP Study ID unavailable; enter it after opening the study.';
      }
    } catch (error) {
      if (isCancelled()) throw new Error('Folder scan cancelled.');
      metadataWarning = `Study ID unavailable: ${error.message}`;
    }
    result.push({ ...study, studyId, metadataWarning, parsedMetadata, repRevision });
  }
  return newestStudies(result);
}

// Check REP size/modified time; parse contents only when new or changed.
// Retain older candidate metadata so deduplication does not cause repeated parsing.
const metadataCache = new WeakMap();
export async function mergeStudyIds(discovered, previous, isCancelled = () => false) {
  const known = new Map(
    studyExports(metadataCache.get(previous) || previous).map((study) => [study.id, study])
  );
  const result = new Array(discovered.length);
  let cursor = 0;
  await Promise.all(
    Array.from({ length: Math.min(8, discovered.length) }, async () => {
      while (cursor < discovered.length) {
        const index = cursor;
        cursor += 1;
        const study = discovered[index];
        if (isCancelled()) throw new Error('Folder scan cancelled.');
        const old = known.get(study.id);
        let currentFile = null;
        try {
          if (study.rep)
            currentFile = study.rep.handle ? await study.rep.handle.getFile() : study.rep;
        } catch {
          /* loadStudyIds reports an unavailable REP below. */
        }
        if (
          old?.rep &&
          currentFile &&
          old.parsedMetadata?.parserVersion === REP_PARSER_VERSION &&
          sameFileRevision(old.repRevision, currentFile)
        ) {
          result[index] = {
            ...study,
            studyId: old.studyId,
            parsedMetadata: old.parsedMetadata,
            repRevision: old.repRevision,
            metadataWarning: old.metadataWarning,
          };
        } else {
          const readable = currentFile ? { ...study, rep: currentFile } : study;
          [result[index]] = await loadStudyIds([readable], isCancelled);
          result[index].rep = study.rep;
        }
      }
    })
  );
  const latest = newestStudies(result);
  metadataCache.set(latest, result);
  return latest;
}
